import type { SecurityOptions, PasscodeOptions, FormatOptions, ShareFormat } from '@/types/share'
import type { EditorState } from './editor-state'
import type { EditorAction } from './editor-actions'

// Default option constants — exported for use by useDefaultShareOptions and other consumers
export const DEFAULT_SECURITY_OPTIONS: SecurityOptions = {
  expiresIn: '1d',
  burnAfterRead: false,
  isModifiable: false,
}

export const DEFAULT_PASSCODE_OPTIONS: PasscodeOptions = {
  passcode: '',
  embedInUrl: true,
  useCustomPasscode: false,
  isPublic: false,
}

export const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  format: 'md' as ShareFormat,
}

export function createInitialState(overrides: Partial<EditorState> = {}): EditorState {
  return {
    content: '',
    filename: 'untitled.md',
    originalContent: '',
    viewMode: 'split',
    sidebarCollapsed: false,
    showRawView: false,
    viewingShareId: undefined,
    viewingShareMeta: null,
    selectedFileId: undefined,
    currentFileType: 'draft',
    currentDraftId: null,
    createdShare: null,
    securityOptions: DEFAULT_SECURITY_OPTIONS,
    passcodeOptions: DEFAULT_PASSCODE_OPTIONS,
    formatOptions: DEFAULT_FORMAT_OPTIONS,
    turnstileToken: null,
    loadingOperation: null,
    ...overrides,
  }
}

/** The empty/default editor state for content fields */
const EMPTY_CONTENT_STATE = {
  currentDraftId: null,
  content: '',
  filename: 'untitled.md',
  originalContent: '',
  viewingShareId: undefined,
  viewingShareMeta: null,
  selectedFileId: undefined,
  currentFileType: 'draft' as const,
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    // --- Compound content transitions ---

    case 'LOAD_DRAFT':
      return {
        ...state,
        currentDraftId: action.draftId,
        content: action.content,
        filename: action.filename,
        originalContent: action.content,
        viewingShareId: undefined,
        viewingShareMeta: null,
        selectedFileId: undefined,
        currentFileType: 'draft',
      }

    case 'LOAD_SHARE':
      return {
        ...state,
        content: action.content,
        filename: action.filename,
        originalContent: action.content,
        viewingShareId: action.shareId,
        viewingShareMeta: action.meta,
        currentDraftId: null,
        currentFileType: action.fileType,
        viewMode: 'preview',
        selectedFileId: action.shareId,
      }

    case 'LOAD_CONTENT':
      return {
        ...state,
        currentDraftId: action.draftId,
        content: action.content,
        filename: action.filename,
        originalContent: action.content,
        selectedFileId: undefined,
        viewingShareId: undefined,
        viewingShareMeta: null,
        currentFileType: 'draft',
        ...(action.format ? { formatOptions: { format: action.format } } : {}),
      }

    case 'RESET_EDITOR':
      return {
        ...state,
        ...EMPTY_CONTENT_STATE,
      }

    case 'POST_PUBLISH_CLEANUP': {
      const base = {
        ...state,
        createdShare: null,
        turnstileToken: null,
        viewMode: action.defaultViewMode,
        securityOptions: DEFAULT_SECURITY_OPTIONS,
        passcodeOptions: DEFAULT_PASSCODE_OPTIONS,
        formatOptions: DEFAULT_FORMAT_OPTIONS,
        viewingShareId: undefined,
        viewingShareMeta: null,
        selectedFileId: undefined,
        currentFileType: 'draft' as const,
      }
      if (action.nextDraftId) {
        return {
          ...base,
          currentDraftId: action.nextDraftId,
          content: action.content,
          filename: action.filename,
          originalContent: action.content,
        }
      }
      return {
        ...base,
        currentDraftId: null,
        content: '',
        filename: 'untitled.md',
        originalContent: '',
      }
    }

    // --- Granular field updates ---

    case 'SET_CONTENT':
      return { ...state, content: action.content }

    case 'SET_FILENAME':
      return { ...state, filename: action.filename }

    case 'SET_ORIGINAL_CONTENT':
      return { ...state, originalContent: action.originalContent }

    // --- Draft management ---

    case 'SET_CURRENT_DRAFT_ID':
      return { ...state, currentDraftId: action.draftId }

    case 'DETACH_FROM_SHARE':
      return {
        ...state,
        currentDraftId: action.draftId,
        filename: action.filename,
        viewingShareId: undefined,
        viewingShareMeta: null,
        selectedFileId: undefined,
      }

    // --- View/Layout ---

    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.viewMode }

    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed }

    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.collapsed }

    case 'TOGGLE_RAW_VIEW':
      return { ...state, showRawView: !state.showRawView }

    // --- Share viewing ---

    case 'SET_SELECTED_FILE_ID':
      return { ...state, selectedFileId: action.fileId }

    case 'ENTER_EDIT_MODE':
      return { ...state, viewMode: 'split' }

    // --- Share creation flow ---

    case 'SET_SECURITY_OPTIONS':
      return { ...state, securityOptions: action.options }

    case 'SET_PASSCODE_OPTIONS': {
      // When switching to public, cascade: reset burnAfterRead and isModifiable
      if (action.options.isPublic && !state.passcodeOptions.isPublic) {
        return {
          ...state,
          passcodeOptions: action.options,
          securityOptions: {
            ...state.securityOptions,
            burnAfterRead: false,
            isModifiable: false,
          },
        }
      }
      return { ...state, passcodeOptions: action.options }
    }

    case 'SET_FORMAT_OPTIONS':
      return { ...state, formatOptions: action.options }

    case 'SET_TURNSTILE_TOKEN':
      return { ...state, turnstileToken: action.token }

    case 'CREATE_SHARE_SUCCESS':
      return {
        ...state,
        createdShare: action.data,
        turnstileToken: null,
        loadingOperation: null,
      }

    case 'RESET_SHARE_OPTIONS':
      return {
        ...state,
        securityOptions: action.security,
        passcodeOptions: action.passcode,
        formatOptions: action.format,
      }

    case 'LOAD_SHARE_OPTIONS':
      return {
        ...state,
        securityOptions: action.security,
        passcodeOptions: action.passcode,
        formatOptions: action.format,
      }

    // --- Loading ---

    case 'SET_LOADING':
      return { ...state, loadingOperation: action.operation }

    // --- File type ---

    case 'SET_FILE_TYPE':
      return { ...state, currentFileType: action.fileType }
  }
}
